import React, { CSSProperties, FC, ReactNode, useContext } from 'react';
import classNames from 'classnames';
import stringUtils from '../../utils/string';
import classes from './styles/index.module.scss';
import { codes } from '../../data/translations';
import { TranslationContext } from '../../modules/translations';
import PreviewFieldHTML from './PreviewFieldHTML';
import PreviewFieldLink from './PreviewFieldLink';
import PreviewFieldDefault from './PreviewFieldDefault';
import PreviewFieldCollapsibleText from './PreviewFieldCollapsibleText';
import { ControlGroupValidationSection } from '../ControlGroup/ControlGroup/ControlGroupValidationSection';
import { PreviewFieldLabel } from './PreviewFieldLabel';
import { isUndefined } from '../../predicates/type';

export const HELLOFLEX_PREVIEW_FIELD_CLASS = 'helloflex-preview-field';

export type PreviewFieldProps = {
	className?: string;
	label?: string | JSX.Element;
	value?: string | number | ReactNode;
	placeholder?: string;
	placeholderTranslationCode?: number;
	hasBottomLine?: boolean;
	insertAsHTML?: boolean;
	previewUrl?: string | null;
	previewUrlTarget?: '_blank' | '_self';
	hidePreviewLabel?: boolean;
	hidden?: boolean;
	wrapText?: boolean;
	dataTestId?: string;
	collapsibleText?: boolean;
	labelBold?: boolean;
	format?: (value: ReactNode) => ReactNode;
	tooltip?: ReactNode;
	length?: number;
	validationMessage?: string;
	style?: CSSProperties;
	fullValueTooltip?: boolean;
	forceUseLink?: boolean;
	textEllipsis?: boolean;
};

export const PreviewField: FC<React.PropsWithChildren<PreviewFieldProps>> = ({
	label = '',
	labelBold,
	value = '',
	className,
	style,
	placeholder = '',
	hasBottomLine = true,
	insertAsHTML,
	tooltip,
	previewUrl,
	previewUrlTarget,
	hidePreviewLabel,
	hidden,
	dataTestId,
	forceUseLink = false,
	placeholderTranslationCode = codes.common.empty,
	wrapText,
	collapsibleText,
	length,
	validationMessage,
	format,
	fullValueTooltip,
	textEllipsis,
}) => {
	const { translate } = useContext(TranslationContext);

	if (hidden) return null;

	const defaultPlaceholder = translate(placeholderTranslationCode);
	const componentClasses = classNames(classes.previewField, HELLOFLEX_PREVIEW_FIELD_CLASS, className);
	const isLink = !!previewUrl;
	const isDangerouslySetHTML = !!insertAsHTML;
	const isCollapsibleText = !!collapsibleText;
	const hasValidationMessage = !!validationMessage;

	const valueClasses = classNames(classes.value, {
		[classes.placeholder]: stringUtils.isNullOrEmpty(value),
		[classes.value_hasBottomLine]: hasBottomLine,
		[classes.value_wrapText]: wrapText || collapsibleText,
		[classes.value_textEllipsis]: textEllipsis,
	});

	const formatValue = isUndefined(format) ? value : format(value);

	const getPreviewValue = () => {
		switch (true) {
			case isDangerouslySetHTML:
				return (
					<PreviewFieldHTML
						value={formatValue}
						valueClasses={valueClasses}
						defaultPlaceholder={defaultPlaceholder}
						placeholder={placeholder}
					/>
				);
			case isLink:
				return (
					<PreviewFieldLink
						value={formatValue}
						forceUseLink={forceUseLink}
						valueClasses={valueClasses}
						previewUrl={previewUrl}
						previewUrlTarget={previewUrlTarget}
					/>
				);
			case isCollapsibleText:
				return (
					<PreviewFieldCollapsibleText
						value={formatValue}
						length={length}
						valueClasses={valueClasses}
						placeholder={placeholder}
						defaultPlaceholder={defaultPlaceholder}
					/>
				);
			default:
				return (
					<PreviewFieldDefault
						value={formatValue}
						valueClasses={valueClasses}
						placeholder={placeholder}
						defaultPlaceholder={defaultPlaceholder}
						fullValueTooltip={fullValueTooltip}
					/>
				);
		}
	};
	const showLabel = label && !hidePreviewLabel;

	return (
		<div
			className={componentClasses}
			style={style}
			data-testid={dataTestId}
		>
			{showLabel && (
				<PreviewFieldLabel
					label={label}
					labelBold={labelBold}
					tooltip={tooltip!}
				/>
			)}
			{hasValidationMessage ? (
				<ControlGroupValidationSection
					validationMessage={validationMessage}
					validationMessagePlace='component'
				>
					{getPreviewValue()}
				</ControlGroupValidationSection>
			) : (
				getPreviewValue()
			)}
		</div>
	);
};
